/**
 * ServiceTypeSelect Component
 * Specialized select field for service types with icons
 */

import React, { forwardRef } from 'react';
import { Controller, Control, FieldError } from 'react-hook-form';
import styled from 'styled-components';
import { FieldWrapper } from './FieldWrapper';
import { ClearButton } from './ClearButton';
import { buildAriaDescribedBy, useFormIds } from './utils';
import { SERVICE_TYPE_OPTIONS } from '../../types/forms';

// ============================================================================
// Types
// ============================================================================

interface ServiceTypeSelectProps {
  name: string;
  control?: Control<any>;
  label: string;
  placeholder?: string;
  error?: FieldError | undefined;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  'data-testid'?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const SelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Select = styled.select<{
  hasError: boolean;
  disabled?: boolean;
}>`
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 2.5rem;
  min-height: 3rem; /* 48px touch target */
  border: 1px solid
    ${(props) => (props.hasError ? 'var(--danger-600)' : 'var(--border)')};
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--surface-strong);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem 1.25rem;
  appearance: none;
  transition: border-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--brand-300);
  }

  &:focus {
    outline: none;
    border-color: var(--brand-500);
    box-shadow: 0 0 0 3px var(--brand-100);
  }

  &:disabled {
    background-color: var(--surface-muted);
    color: var(--text-disabled);
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Hide default arrow in IE */
  &::-ms-expand {
    display: none;
  }

  /* Mobile: Ensure touch targets */
  @media (max-width: 767px) {
    min-height: 3rem; /* 48px */
  }

  @media (max-width: 479px) {
    min-height: 3.25rem; /* 52px for small mobile */
  }
`;

const ClearButtonWrapper = styled.div`
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
`;

// ServiceTypeOption removed - HTML select options don't support JSX content

// ============================================================================
// Component
// ============================================================================

/**
 * ServiceTypeSelect - Select field specifically for service types with icons
 */
export const ServiceTypeSelect = forwardRef<
  HTMLSelectElement,
  ServiceTypeSelectProps
>(
  (
    {
      name,
      control,
      label,
      placeholder = 'Selecciona un tipo de servicio',
      error,
      helperText,
      required = false,
      disabled = false,
      clearable = false,
      className,
      'data-testid': testId,
    },
    ref,
  ) => {
    const { inputId, errorId, helperId } = useFormIds(name);
    const fieldId = inputId || `service-type-${name}-${Date.now()}`;

    const ariaDescribedBy = buildAriaDescribedBy([
      error ? errorId : undefined,
      helperText ? helperId : undefined,
    ]);

    // Note: HTML select options don't support JSX content with icons

    if (control) {
      return (
        <FieldWrapper
          id={fieldId}
          label={label}
          error={error?.message}
          helperText={helperText}
          required={required}
          className={className}
        >
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <SelectContainer>
                <Select
                  {...field}
                  id={fieldId}
                  ref={ref}
                  hasError={Boolean(error)}
                  disabled={disabled}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={Boolean(error)}
                  aria-required={required}
                  data-testid={testId}
                >
                  {placeholder && (
                    <option value="" disabled>
                      {placeholder}
                    </option>
                  )}
                  {SERVICE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                {clearable && field.value && (
                  <ClearButtonWrapper>
                    <ClearButton
                      onClear={() => field.onChange('')}
                      disabled={disabled}
                      aria-label={`Clear ${label || 'service type'} selection`}
                    />
                  </ClearButtonWrapper>
                )}
              </SelectContainer>
            )}
          />
        </FieldWrapper>
      );
    }

    // Render as uncontrolled component
    return (
      <FieldWrapper
        id={fieldId}
        label={label || 'Service Type'}
        error={error?.message}
        helperText={helperText}
        required={required}
        className={className}
      >
        <SelectContainer>
          <Select
            id={fieldId}
            name={name}
            ref={ref}
            hasError={Boolean(error)}
            disabled={disabled}
            aria-describedby={ariaDescribedBy}
            aria-invalid={Boolean(error)}
            aria-required={required}
            data-testid={testId}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {SERVICE_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          {clearable && (
            <ClearButtonWrapper>
              <ClearButton
                onClear={() => {
                  const select = ref as React.RefObject<HTMLSelectElement>;
                  if (select.current) {
                    select.current.value = '';
                    select.current.dispatchEvent(
                      new Event('change', { bubbles: true }),
                    );
                  }
                }}
                disabled={disabled}
                aria-label={`Clear ${label || 'service type'} selection`}
              />
            </ClearButtonWrapper>
          )}
        </SelectContainer>
      </FieldWrapper>
    );
  },
);

ServiceTypeSelect.displayName = 'ServiceTypeSelect';

export default ServiceTypeSelect;
